import html2canvas from "html2canvas";

/**
 * Renders a polaroid DOM node to a JPEG blob.
 *
 * html2canvas 1.4.1 does not correctly render either the `aspect-ratio` CSS
 * property or `object-fit: cover`. If the live polaroid node is handed to it
 * unchanged the photo comes out stretched to fill its container.
 *
 * Workaround: clone the polaroid, replace the <img> with a CSS background
 * (html2canvas handles `background-size: cover` correctly), and use an
 * explicit width/height on the photo container so no CSS aspect-ratio is
 * needed. The whole polaroid frame is also given an explicit width so
 * off-screen cloning does not change its size.
 */
export async function generatePolaroidPicture(node) {
  const nodeRect = node.getBoundingClientRect();
  const frameW = Math.max(1, Math.round(nodeRect.width));

  const originalPhoto = node.querySelector(".polaroid-photo");
  const photoRect = originalPhoto?.getBoundingClientRect();
  const photoW = Math.max(1, Math.round(photoRect?.width || 0));
  const photoH = Math.max(1, Math.round(photoRect?.height || photoW * 1.25));

  // Preload the image so its bytes are decoded before we rasterise.
  const originalImg = originalPhoto?.querySelector("img");
  const src = originalImg?.src;
  if (src) {
    await new Promise((resolve) => {
      const t = new Image();
      t.crossOrigin = "anonymous";
      t.onload = resolve;
      t.onerror = resolve;
      t.src = src;
    });
  }

  const clone = node.cloneNode(true);
  clone.style.position = "fixed";
  clone.style.left = "-10000px";
  clone.style.top = "0";
  clone.style.margin = "0";
  clone.style.transform = "none"; // drop the tilt for a clean export
  clone.style.width = `${frameW}px`;
  clone.style.maxWidth = "none";

  const clonedPhoto = clone.querySelector(".polaroid-photo");
  const clonedImg = clonedPhoto?.querySelector("img");

  if (clonedPhoto && clonedImg && src) {
    clonedPhoto.style.width = `${photoW}px`;
    clonedPhoto.style.height = `${photoH}px`;
    clonedPhoto.style.aspectRatio = "auto";
    clonedPhoto.style.backgroundImage = `url("${src}")`;
    clonedPhoto.style.backgroundSize = "cover";
    clonedPhoto.style.backgroundPosition = "center";
    clonedPhoto.style.backgroundRepeat = "no-repeat";
    clonedImg.remove();
  }

  document.body.appendChild(clone);
  try {
    const canvas = await html2canvas(clone, {
      backgroundColor: "#ffffff",
      useCORS: true,
      scale: 3, // print-quality
      logging: false,
      width: clone.offsetWidth,
      height: clone.offsetHeight,
    });
    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", 0.95),
    );
    return { blob, dataUrl };
  } finally {
    clone.remove();
  }
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
